export function CostApproach() {
  return (
    <section className="py-20 px-4 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Method #3: Cost Approach
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            The cost approach values your apartment building based on what it would cost to replace it today, minus depreciation. While less commonly used for income-producing properties, this method provides important insights, especially for newer buildings or insurance purposes.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">How the Cost Approach Works</h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              The cost approach calculates property value by estimating the current cost to construct an identical building, then subtracting depreciation based on age, condition, and functional obsolescence. The land value is added separately, as land doesn't depreciate.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              The formula is: <strong>Property Value = Land Value + (Replacement Cost - Depreciation)</strong>. This approach is most useful for newer properties, special-purpose buildings, or when comparable sales data is limited. It's also commonly used for insurance valuations.
            </p>
            <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-r-lg">
              <h4 className="font-bold text-gray-900 mb-2 text-lg">Key Formula:</h4>
              <p className="text-gray-800 text-xl font-semibold mb-2">Value = Land + (Replacement Cost - Depreciation)</p>
              <p className="text-gray-600">Most useful for newer properties or insurance purposes</p>
            </div>
          </div>
          <div>
            <img
              src="https://public.readdy.ai/ai/img_res/91c2045d36e88a8b1c4f6f3c644c2543.jpg"
              alt="Cost approach valuation for apartment buildings"
              className="w-full h-96 object-cover object-top rounded-lg shadow-lg"
            />
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-8 lg:p-12 mb-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">Components of the Cost Approach</h3>
          
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-full mb-4">
                <i className="ri-landscape-line text-green-600 text-xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-3 text-lg">Land Value</h4>
              <p className="text-gray-600 mb-4">Determine the value of the land as if vacant, based on comparable land sales in the area.</p>
              <p className="text-gray-800 font-semibold">Example: $400,000</p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full mb-4">
                <i className="ri-building-line text-teal-600 text-xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-3 text-lg">Replacement Cost</h4>
              <p className="text-gray-600 mb-4">Calculate current construction cost per square foot multiplied by total building area.</p>
              <p className="text-gray-800 font-semibold">Example: $2,400,000</p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full mb-4">
                <i className="ri-time-line text-red-600 text-xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-3 text-lg">Depreciation</h4>
              <p className="text-gray-600 mb-4">Subtract physical deterioration, functional obsolescence, and external obsolescence.</p>
              <p className="text-gray-800 font-semibold">Example: -$600,000</p>
            </div>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-lg p-6 shadow-lg border-2 border-teal-600">
              <h4 className="text-xl font-bold text-gray-900 mb-4 text-center">Cost Approach Calculation Example</h4>
              <div className="space-y-3 text-lg">
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Land Value:</span>
                  <span className="font-semibold text-gray-900">$400,000</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Replacement Cost New:</span>
                  <span className="font-semibold text-gray-900">$2,400,000</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Less: Depreciation (25%):</span>
                  <span className="font-semibold text-red-600">-$600,000</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Depreciated Building Value:</span>
                  <span className="font-semibold text-gray-900">$1,800,000</span>
                </div>
                <div className="flex justify-between items-center pt-3 border-t-2 border-teal-600">
                  <span className="text-gray-900 font-bold text-xl">Total Property Value:</span>
                  <span className="font-bold text-teal-600 text-2xl">$2,200,000</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <img
              src="https://readdy.ai/api/search-image?query=building-depreciation-aging-property-wear-tear-physical-deterioration-older-apartment-building-maintenance-issues-deferred-maintenance-property-condition-assessment-building-inspection-structural-evaluation&width=800&height=600&seq=depreciation-analysis-001&orientation=landscape"
              alt="Building depreciation and condition assessment"
              className="w-full h-96 object-cover object-top rounded-lg shadow-lg"
            />
          </div>
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Understanding Depreciation</h3>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Depreciation in the cost approach accounts for the loss in value from the building's original construction cost. There are three types of depreciation that must be considered when valuing an apartment building.
            </p>
            <div className="space-y-4">
              <div className="bg-white rounded-lg p-5 shadow-md border-l-4 border-red-500">
                <h4 className="font-bold text-gray-900 mb-2">Physical Deterioration</h4>
                <p className="text-gray-600">Wear and tear from age, weather, and use. Includes roof condition, HVAC systems, plumbing, and structural components.</p>
              </div>
              <div className="bg-white rounded-lg p-5 shadow-md border-l-4 border-orange-500">
                <h4 className="font-bold text-gray-900 mb-2">Functional Obsolescence</h4>
                <p className="text-gray-600">Outdated design, poor layout, inadequate parking, small units, or lack of modern amenities that reduce desirability.</p>
              </div>
              <div className="bg-white rounded-lg p-5 shadow-md border-l-4 border-yellow-500">
                <h4 className="font-bold text-gray-900 mb-2">External Obsolescence</h4>
                <p className="text-gray-600">Factors outside the property like declining neighborhood, increased crime, or economic changes in the area.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-lg p-8 lg:p-12 text-white">
          <h3 className="text-3xl font-bold mb-6 text-center">When to Use the Cost Approach</h3>
          <p className="text-lg text-center mb-10 max-w-3xl mx-auto opacity-90">
            While the income approach is typically most important for apartment buildings, the cost approach provides valuable insights in specific situations:
          </p>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <div className="bg-white/10 rounded-lg p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-check-line text-white text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-2">Newer Construction</h4>
                  <p className="opacity-90">Properties less than 5-10 years old with minimal depreciation</p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 rounded-lg p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-check-line text-white text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-2">Insurance Purposes</h4>
                  <p className="opacity-90">Determining replacement cost for insurance coverage</p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 rounded-lg p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-check-line text-white text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-2">Limited Comparable Sales</h4>
                  <p className="opacity-90">When few similar properties have sold recently in your market</p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 rounded-lg p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-check-line text-white text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-2">Special Purpose Properties</h4>
                  <p className="opacity-90">Unique buildings with features not common in the market</p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 rounded-lg p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-check-line text-white text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-2">Highest and Best Use Analysis</h4>
                  <p className="opacity-90">Determining if the current use maximizes the property's value</p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 rounded-lg p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-check-line text-white text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-2">Feasibility Studies</h4>
                  <p className="opacity-90">Evaluating whether new construction or renovation makes economic sense</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 bg-white/10 rounded-lg p-6 max-w-4xl mx-auto">
            <p className="text-lg opacity-90 text-center">
              <strong>Important Note:</strong> For most apartment buildings, the cost approach typically produces the highest value estimate and should be used primarily as a supporting method rather than the primary valuation approach. The income approach remains the most reliable method for income-producing properties.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}